import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PartnershipsPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'From $40 Per Subscriber Every Month Revenue',
      description: 'Earn from $40 for every subscriber, creating a sustainable revenue stream for your organization'
    },
    {
      icon: 'ri-robot-line',
      title: 'Automated Tax Preparation',
      description: 'Subscribers get automated tax preparation for all major forms including 1040, 1065, 1120, and more'
    },
    {
      icon: 'ri-team-line',
      title: 'Subscriber Value',
      description: 'Provide exclusive benefits to your community with professional-grade tax automation software'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Scalable Income',
      description: 'Revenue grows with your community - the more members who subscribe through your link, the more you earn'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'White-Label Options',
      description: 'Customize the experience with your branding and maintain your community identity'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Dedicated Support',
      description: '24/7 AI tax assistant support for all your subscribers plus dedicated partnership manager'
    },
    {
      icon: 'ri-star-line',
      title: 'Make Even More By Creating a Team',
      description: 'Enroll other Partners and get a percentage of what they earn automatically'
    }
  ];

  const idealPartners = [
    {
      icon: 'ri-building-line',
      title: 'Professional Associations',
      description: 'CPA societies, accounting associations, and tax professional organizations',
      examples: ['State CPA Societies', 'Tax Professional Networks', 'Accounting Associations']
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Educational Communities',
      description: 'Accounting programs, tax certification courses, and professional development groups',
      examples: ['University Accounting Programs', 'Tax Certification Courses', 'Continuing Education Providers']
    },
    {
      icon: 'ri-group-line',
      title: 'Large Tax Firms',
      description: 'Multi-location tax preparation firms and accounting practices',
      examples: ['Regional Tax Firms', 'Franchise Tax Services', 'Accounting Practice Groups']
    },
    {
      icon: 'ri-community-line',
      title: 'Online Communities',
      description: 'Tax and accounting forums, membership sites, and professional networks',
      examples: ['Tax Professional Forums', 'Accounting Membership Sites', 'LinkedIn Groups']
    }
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Apply for Partnership',
      description: 'Submit your organization details and member information through our partnership application'
    },
    {
      step: '2',
      title: 'Custom Setup',
      description: 'We create a customized partnership portal with your branding and unique member signup link'
    },
    {
      step: '3',
      title: 'Launch to Subscribers',
      description: 'Share the exclusive offer with your community through email, newsletters, or member portal'
    },
    {
      step: '4',
      title: 'Earn Revenue',
      description: 'Receive $40 per active subscription monthly, with transparent reporting and automated payments'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/')} className="flex items-center hover:opacity-90 transition-opacity cursor-pointer">
              <img 
                alt="InstaVisible Tax & Savings Solutions Logo" 
                className="h-14 w-auto object-contain" 
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
              />
            </button>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-1">
              <button onClick={() => navigate('/')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Home</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('services'), 100); }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Services</button>
              <button onClick={() => navigate('/ai-agents')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">AI Agents</button>
              <button onClick={() => navigate('/partnerships')} className="px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 transition-colors rounded-lg whitespace-nowrap">Partnerships</button>
              <button onClick={() => navigate('/blog')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Blog</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('contact'), 100); }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Contact</button>
              <div className="w-px h-6 bg-gray-300 mx-2"></div>
              <a href="https://fas.st/t/iddSjFC9" target="_blank" rel="noopener noreferrer" className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-lg text-sm transition-all h-9 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 font-medium ml-2">Become a Partner</a>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-2">
                <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Home</button>
                <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('services'), 100); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Services</button>
                <button onClick={() => { navigate('/ai-agents'); setMobileMenuOpen(false); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">AI Agents</button>
                <button onClick={() => { navigate('/partnerships'); setMobileMenuOpen(false); }} className="px-4 py-3 text-sm font-medium text-emerald-700 bg-emerald-50 transition-colors rounded-lg text-left whitespace-nowrap">Partnerships</button>
                <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Blog</button>
                <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('contact'), 100); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Contact</button>
                <a href="https://fas.st/t/iddSjFC9" target="_blank" rel="noopener noreferrer" className="mx-4 mt-2 inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-lg text-sm transition-all h-11 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 font-medium">Become a Partner</a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/e81ec4a815a297e490291917efba52b0.jpg')`
          }}
        ></div>
        
        <div className="relative z-10 max-w-5xl mx-auto text-center w-full">
          <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm text-emerald-700 px-5 py-2.5 rounded-full text-sm font-semibold mb-8 shadow-lg">
            <i className="ri-shield-check-line text-base"></i>
            <span>Trusted Partner Program</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 tracking-tight leading-tight drop-shadow-2xl">
            Partner with <span className="text-emerald-400">AiTaxForms</span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-white/95 mb-12 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
            Create a sustainable revenue stream for your tax professional community or accounting organization while providing subscribers with cutting-edge automated tax prep software. Earn from $40 monthly for every active subscription.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a href="https://fas.st/t/iddSjFC9" target="_blank" rel="noopener noreferrer" className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-emerald-600 text-white hover:bg-emerald-700 text-base font-semibold px-8 h-14 rounded-xl shadow-lg shadow-emerald-600/30 hover:shadow-emerald-700/40 transition-all duration-300 hover:scale-105">
              Apply for Partnership
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
            <button onClick={() => scrollToSection('how-it-works')} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap text-base font-semibold px-8 h-14 rounded-xl border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 transition-all duration-300">
              How It Works
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 pt-8">
            <div>
              <div className="text-3xl font-bold text-emerald-600">$40+</div>
              <div className="text-sm text-gray-600">Per Subscriber/Month</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-600">100%</div>
              <div className="text-sm text-gray-600">Automated</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-600">24/7</div>
              <div className="text-sm text-gray-600">Subscriber Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 tracking-tight">Partnership Benefits</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to build a successful tax software community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Scalable Income */}
            <div className="p-8 bg-gradient-to-br from-emerald-50 to-white border-2 border-gray-200 hover:border-emerald-400 transition-all duration-300 rounded-2xl">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Scalable Income</h3>
              <p className="text-gray-600 leading-relaxed">
                Revenue grows with your community - the more members who subscribe through your link, the more you earn
              </p>
            </div>

            {/* Dedicated Support */}
            <div className="p-8 bg-gradient-to-br from-teal-50 to-white border-2 border-emerald-400 transition-all duration-300 rounded-2xl shadow-lg">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-customer-service-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Dedicated Support</h3>
              <p className="text-gray-600 leading-relaxed">
                24/7 AI tax assistant support for all your subscribers plus dedicated partnership manager
              </p>
            </div>

            {/* Make Even More By Creating a Team */}
            <div className="p-8 bg-gradient-to-br from-emerald-50 to-white border-2 border-gray-200 hover:border-emerald-400 transition-all duration-300 rounded-2xl">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-star-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Make Even More By Creating a Team</h3>
              <p className="text-gray-600 leading-relaxed">
                Enroll other Partners and get a percentage of what they earn automatically
              </p>
            </div>

            {/* Automated Tax Preparation */}
            <div className="p-8 bg-gradient-to-br from-emerald-50 to-white border-2 border-gray-200 hover:border-emerald-400 transition-all duration-300 rounded-2xl">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-file-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Automated Tax Preparation</h3>
              <p className="text-gray-600 leading-relaxed">
                Subscribers get automated tax preparation for all major forms including 1040, 1065, 1120, and more
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal Partners Section */}
      <section className="py-24 px-6 lg:px-8 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight">Ideal Partner Organizations</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We partner with <strong>tax professional communities</strong>, <strong>accounting associations</strong>, and organizations serving tax preparers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {idealPartners.map((partner, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-emerald-400 transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className={`${partner.icon} text-3xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{partner.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{partner.description}</p>
                    <div className="space-y-2">
                      {partner.examples.map((example, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <i className="ri-check-line text-emerald-600"></i>
                          <span>{example}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple three-step process to start earning revenue from your professional community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative group">
              <div className="bg-gradient-to-br from-emerald-50 to-white p-10 rounded-2xl border-2 border-gray-200 hover:border-emerald-400 transition-all duration-500 hover:shadow-xl h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                  <i className="ri-user-add-line text-3xl text-white"></i>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-5xl font-bold text-emerald-600">01</span>
                  <h3 className="text-2xl font-bold text-gray-900">Apply & Get Approved</h3>
                </div>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Submit your application and get approved within 24-48 hours. Receive your unique tracking link and partner dashboard access.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="bg-gradient-to-br from-emerald-50 to-white p-10 rounded-2xl border-2 border-gray-200 hover:border-emerald-400 transition-all duration-500 hover:shadow-xl h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                  <i className="ri-share-forward-line text-3xl text-white"></i>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-5xl font-bold text-emerald-600">02</span>
                  <h3 className="text-2xl font-bold text-gray-900">Launch to Subscribers</h3>
                </div>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Share your affiliate link with your community through email, newsletters, or member portal (we can do it for you)
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="bg-gradient-to-br from-emerald-50 to-white p-10 rounded-2xl border-2 border-gray-200 hover:border-emerald-400 transition-all duration-500 hover:shadow-xl h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                  <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-5xl font-bold text-emerald-600">03</span>
                  <h3 className="text-2xl font-bold text-gray-900">Earn Revenue</h3>
                </div>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Earn from $40 monthly for every active subscriber. Track your performance in real-time through your partner dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Ready to Create a New Revenue Stream?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Join leading <strong>tax professional organizations</strong> and <strong>accounting communities</strong> earning sustainable revenue while providing exceptional value to subscribers
          </p>
          <a href="https://fas.st/t/iddSjFC9" target="_blank" rel="noopener noreferrer" className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-white text-emerald-700 hover:bg-gray-50 text-base font-semibold px-8 h-14 rounded-xl shadow-lg transition-all duration-300 hover:scale-105">
            Apply for Partnership
            <i className="ri-arrow-right-line text-lg"></i>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <img 
                  alt="InstaVisible Tax & Savings Solutions Logo" 
                  className="h-14 w-auto object-contain brightness-0 invert" 
                  src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
                />
              </div>
              <p className="text-white/90 leading-relaxed max-w-md">
                <strong>Professional AI tax software</strong> for CPAs, tax professionals and CFOs. Streamline your workflow with cutting-edge <strong>automated tax preparation</strong> and <strong>AI-powered tax intelligence</strong>.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Product</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Services</a></li>
                <li><a href="/ai-agents" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">AI Agents</a></li>
                <li><a href="https://itsjusttax.com" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Blog</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Company</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Contact</a></li>
                <li><a href="/partnerships" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Partnerships</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-6">Resources</h3>
              <ul className="space-y-4">
                <li><a href="/demo-video" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Demo Video</a></li>
                <li><span className="text-white/80">Automated tax preparation for 1040, 1065, 1120</span></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white/80">© 2025 InstaVisible Tax & Savings Solutions. All rights reserved.</p>
            <div className="flex items-center gap-6 sm:gap-8 text-white/80 flex-wrap justify-center">
              <a href="https://my-web-1763232775898.estage.site/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a>
              <a href="https://my-web-1763232775898.estage.site/terms-of-use" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Terms of Use</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
